// Transform a key to a json logic expression
export type Value =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | boolean[]
  | null;

// Create comparison logic based on value type
export const createComparison = (
  varPath: string,
  val: Value,
  isSearch: boolean = false
): any => {
  if (!val || (Array.isArray(val) && val.length === 0)) {
    return undefined;
  }
  const value = Array.isArray(val) ? val : [val];

  if (isSearch) {
    return { in: [value[0], { var: varPath }] };
  }

  if (value.length > 1) {
    // For array values, use "in" operator
    return { in: [{ var: varPath }, val] };
  } else {
    // For single values, use "==" operator
    return { '==': [{ var: varPath }, value[0]] };
  }
};

// Wrap a result with "some" operators for each array segment, from innermost to outermost
export const wrapArraySegments = (
  arraySegments: string[],
  innermost: any
): any => {
  let result = innermost;
  for (let i = arraySegments.length - 1; i >= 0; i--) {
    result = {
      some: [{ var: arraySegments[i] }, result],
    };
  }
  return result;
};

/**
 * Transform a key to a json logic expression based on key and value
 * Examples:
 * product.inn, "1" => {"==": [{"var":"product.inn"}, "1" ]}
 * product.inn, ["1", "2"] => {"in": [{"var":"product.inn"}, ["1", "2"]]}
 * tests[].performer.id, "1" =>
 * {"some": [{"var": "tests"}, {"==": [{"var": "performer.id"}, "1"]}]}
 * tests[].performer.id, ["1", "2"] =>
 * {"some": [{"var": "tests"}, {"in": [{"var": "performer.id"}, ["1", "2"]]}]}
 */
const transformKeyToJsonLogic = (
  key: string,
  value: Value,
  isSearch: boolean = false
): any => {
  // Split the key by "[]" to find array segments
  const segments = key.split('[]');

  if (segments.length === 1) {
    // No arrays in the path - simple case
    return createComparison(key, value, isSearch);
  }

  // Start with the last segment (after the last [])
  const lastSegment = segments[segments.length - 1];
  const remainingPath = lastSegment.startsWith('.')
    ? lastSegment.slice(1)
    : lastSegment;

  // Create the innermost comparison
  const innermost = createComparison(remainingPath, value, isSearch);

  // Remove leading dots from the array segments (segments after the first [] have leading dots)
  const arraySegments = segments
    .slice(0, -1)
    .map(segment => (segment.startsWith('.') ? segment.slice(1) : segment));

  return wrapArraySegments(arraySegments, innermost);
};

export default transformKeyToJsonLogic;
